/**
 * PropertyListTable Component
 * 
 * Displays a simplified table of artifact properties with only property_key and type columns.
 * Used in split panel layout for company model properties.
 */

import React from 'react';
import { Table, Card, Input, Space, Button, Typography, Tag } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { ArtifactProperty } from '../../types/artifactItems';
import type { ColumnsType } from 'antd/es/table';

const { Search } = Input;
const { Title } = Typography;

interface PropertyListTableProps {
  properties: ArtifactProperty[];
  loading: boolean;
  searchText: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  onRowClick?: (property: ArtifactProperty) => void;
  selectedPropertyId?: string | null;
  globalVisibilityProperties?: Record<string, any>;
  onViewGlobalProperty?: (propertyKey: string) => void;
}

export const PropertyListTable: React.FC<PropertyListTableProps> = ({
  properties,
  loading,
  searchText,
  onSearchChange,
  onRefresh,
  onRowClick,
  selectedPropertyId,
  globalVisibilityProperties = {},
  onViewGlobalProperty,
}) => {
  // Filter properties based on search text
  const filteredProperties = React.useMemo(() => {
    if (!searchText) return properties;
    const lowerSearch = searchText.toLowerCase();
    return properties.filter(
      (prop) =>
        prop.property_key.toLowerCase().includes(lowerSearch) ||
        prop.property_type.toLowerCase().includes(lowerSearch)
    );
  }, [properties, searchText]);

  const columns: ColumnsType<ArtifactProperty> = [
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key',
      render: (text: string, record: ArtifactProperty) => (
        <Space>
          <span>{text}</span>
          {globalVisibilityProperties[text] && (
            <Tag color="green" title="Global Visibility Property">Global</Tag>
          )}
          {onViewGlobalProperty && (
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewGlobalProperty(text);
              }}
              title="View Global Property Definition"
            >
              View
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      render: (type: string) => (
        <Tag style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{type}</Tag>
      ),
    },
  ];

  const tableProps: any = {
    columns,
    dataSource: filteredProperties,
    loading,
    size: 'small' as const,
    rowKey: 'id',
    pagination: {
      pageSize: 20,
      showSizeChanger: true,
      showTotal: (total: number) => `Total ${total} properties`,
      position: ['bottomCenter'] as const,
    },
    scroll: { y: 'calc(100vh - 400px)' },
  };

  if (onRowClick) {
    tableProps.onRow = (record: ArtifactProperty) => ({
      onClick: () => onRowClick(record),
      style: {
        cursor: 'pointer',
        backgroundColor: selectedPropertyId === record.id ? '#e6f7ff' : 'transparent',
      },
    });
    tableProps.rowClassName = selectedPropertyId
      ? (record: ArtifactProperty) =>
          selectedPropertyId === record.id ? 'selected-property-row' : ''
      : undefined;
  }

  const title = (
    <Space>
      <Title level={4} style={{ margin: 0 }}>
        Properties
      </Title>
      <Tag>{filteredProperties.length} properties</Tag>
    </Space>
  );

  return (
    <Card
      title={title}
      extra={
        <Space>
          <Search
            placeholder="Search properties..."
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={onRefresh} size="small">
            Refresh
          </Button>
        </Space>
      }
      className="properties-list-card"
    >
      {filteredProperties.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          {searchText ? 'No properties match your search' : 'No properties found'}
        </div>
      ) : (
        <Table {...tableProps} />
      )}
    </Card>
  );
};
